import React, { useEffect, useRef } from 'react';
import { Box, Button, IconButton, Paper, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type { Step } from 'react-joyride';

import { useTourHint } from './TourHintContext';

/**
 * Joyride v3 tooltip render props (subset of what we use).
 *
 * We intentionally do not import the real `TooltipRenderProps` type from
 * `react-joyride` for the component prop signature because the runtime
 * `onClick` type uses `MouseEventHandler<HTMLElement>`. Loosening the
 * signature keeps the component simple to test in isolation while still
 * being structurally compatible with the value Joyride passes.
 */
export interface TourFooterProps {
  continuous?: boolean;
  index: number;
  size: number;
  isLastStep: boolean;
  step: Step;
  backProps: {
    onClick: (event: React.MouseEvent<HTMLElement>) => void;
    'aria-label'?: string;
    title?: string;
  };
  closeProps: {
    onClick: (event: React.MouseEvent<HTMLElement>) => void;
    'aria-label'?: string;
    title?: string;
  };
  primaryProps: {
    onClick: (event: React.MouseEvent<HTMLElement>) => void;
    'aria-label'?: string;
    title?: string;
  };
  skipProps: {
    onClick: (event: React.MouseEvent<HTMLElement>) => void;
    'aria-label'?: string;
    title?: string;
  };
  tooltipProps?: React.HTMLAttributes<HTMLDivElement>;
}

/**
 * Custom tooltip rendered by Joyride for the Product Discovery walkthrough.
 */
const TourFooter: React.FC<TourFooterProps> = ({
  index,
  isLastStep,
  step,
  backProps,
  primaryProps,
  tooltipProps,
}) => {
  const { isHintMode, onDismissHint, onRequestExit, advanceBlocked } =
    useTourHint();

  // Ref to the visible tooltip/bubble Paper. Centering the BUBBLE itself (not
  // the step's target) keeps the bubble in frame on every step of every tour —
  // tall highlighted targets (upload dropzones, results tables, document
  // viewers) can no longer push it off-screen. Because TourFooter is the
  // `tooltipComponent` rendered for EVERY step of EVERY tour, this is the single
  // place that frames the bubble; per-tour components no longer scroll targets.
  const paperRef = useRef<HTMLDivElement | null>(null);

  // Center the bubble on mount and whenever the active step changes. Guarding on
  // step identity (`step.target` + `index`) ensures it runs ONCE per step, not on
  // every render, so there is no scroll loop / jank. `behavior: "auto"` is an
  // instant scroll (no animation). Joyride uses Floating UI's default `absolute`
  // strategy with `skipScroll: true`, so scrolling the bubble into view also
  // moves the anchored target — Joyride keeps the bubble pinned to it.
  // jsdom does not implement scrollIntoView, so we optional-chain the call.
  // A step can opt out of the default center alignment via `data.bubbleAlign`
  // (a `ScrollLogicalPosition`). The homepage "What's new" step uses `"start"`
  // so the bubble sits near the TOP of the viewport, clear of the slide content
  // it would otherwise cover. Defaults to `"center"`, so every other step is
  // unchanged.
  const bubbleAlign =
    (step.data as { bubbleAlign?: ScrollLogicalPosition } | undefined)
      ?.bubbleAlign ?? 'center';

  useEffect(() => {
    // TOP-PINNED steps (`bubbleAlign === "start"`, e.g. the homepage What's-new
    // step over the fixed slide modal) cannot be framed by scrolling: the target
    // is a FIXED modal, and Floating-UI's floater carries a `transform` that
    // makes any `position: fixed` descendant resolve against the floater instead
    // of the viewport. So instead we re-pin the FLOATER element itself to the
    // top-center of the viewport, overriding Floating-UI's inline coords. This
    // keeps the bubble's natural width (it stays in the floater's flow) while
    // lifting it clear of the slides. We re-apply across two rAFs because
    // Floating-UI positions the floater asynchronously after mount.
    //
    // The effect is keyed on `isHintMode` too: when the user clicks the X on the
    // What's-new step the tour flips into hint mode WITHOUT changing the step or
    // index, which re-renders the floater's inner body ("Walkthrough paused")
    // and prompts Floating-UI to reposition the floater (it would otherwise jump
    // to the right edge / off-screen and lose our inline `!important` pin). By
    // re-running here we re-assert the fixed pin so the bubble stays top-center.
    // A MutationObserver on the floater's `style` additionally re-pins if
    // Floating-UI clobbers the inline coords between renders. All of this is
    // scoped to `bubbleAlign === 'start'`, so other tours/steps are untouched.
    if (bubbleAlign === 'start') {
      const floater = () =>
        paperRef.current?.closest('.react-joyride__floater') as
          | HTMLElement
          | null;
      const isPinned = (f: HTMLElement) =>
        f.style.position === 'fixed' &&
        f.style.top === '16px' &&
        f.style.left === '50%' &&
        f.style.transform === 'translateX(-50%)';
      const pin = () => {
        const f = floater();
        if (!f) return;
        // Avoid re-writing identical styles (which would retrigger the
        // MutationObserver in a loop). Only write when something differs.
        if (isPinned(f)) return;
        f.style.setProperty('position', 'fixed', 'important');
        f.style.setProperty('top', '16px', 'important');
        f.style.setProperty('left', '50%', 'important');
        f.style.setProperty('bottom', 'auto', 'important');
        f.style.setProperty('right', 'auto', 'important');
        f.style.setProperty('transform', 'translateX(-50%)', 'important');
      };
      pin();
      let raf2 = 0;
      const raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(pin);
      });
      // Re-assert the pin whenever Floating-UI rewrites the floater's inline
      // style (it sets `transform`/`position` on reposition). Scoped to this
      // step only, so it tears down when the step/hint mode changes.
      const f = floater();
      let observer: MutationObserver | null = null;
      if (f && typeof MutationObserver !== 'undefined') {
        observer = new MutationObserver(() => pin());
        observer.observe(f, {
          attributes: true,
          attributeFilter: ['style'],
        });
      }
      return () => {
        cancelAnimationFrame(raf1);
        if (raf2) cancelAnimationFrame(raf2);
        if (observer) observer.disconnect();
      };
    }

    const scrollToBubble = () =>
      paperRef.current?.scrollIntoView?.({
        block: bubbleAlign,
        inline: 'nearest',
        behavior: 'auto',
      });
    // Center immediately (handles targets already near the viewport), then again
    // after Joyride's Floating UI has positioned the floater at its final spot.
    // Without the deferred pass the early scroll centers a not-yet-positioned
    // bubble, so steps anchored far down a tall page land below the fold.
    scrollToBubble();
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(scrollToBubble);
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step.target, index, bubbleAlign, isHintMode]);

  // Per-step bubble width. The What's-new step opts into a wider bubble via
  // `data.wide` so its copy is shorter vertically (and so it clears the slide
  // caption rendered at the top of the dialog). Every other step keeps 360.
  const wide = Boolean(
    (step.data as { wide?: boolean } | undefined)?.wide
  );
  const paperMaxWidth = wide ? 580 : 360;

  // When the parent has flipped the tour into "hint" mode (i.e. the user
  // exited mid-flow), render the resume-hint layout in-place of the regular
  // walkthrough body. Joyride's overlay stays mounted exactly as-is — only
  // the inner tooltip body re-renders. No close X, no Back/Next.
  if (isHintMode) {
    return (
      <Paper
        ref={paperRef}
        {...tooltipProps}
        elevation={6}
        sx={{
          p: 2,
          maxWidth: paperMaxWidth,
          borderRadius: 2,
          position: 'relative',
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          Walkthrough paused
        </Typography>
        <Typography variant="body2" component="div" sx={{ mb: 2 }}>
          You can resume the walkthrough anytime by clicking the{' '}
          <strong>"Take a tour"</strong> button at the top of this page.
          You'll pick up right where you left off.
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            size="small"
            onClick={onDismissHint}
            aria-label="dismiss-hint"
          >
            Got it!
          </Button>
        </Box>
      </Paper>
    );
  }

  const localeNext = step.locale?.next;
  const localeLast = step.locale?.last;
  const localeBack = step.locale?.back;

  const primaryLabel = isLastStep
    ? (localeLast ?? localeNext ?? 'Finish')
    : (localeNext ?? 'Next');

  const showBack = index > 0;

  // Generic interaction gate: a step opts in via `data.requiresInteraction`.
  // When the owning tour reports the required interaction hasn't happened yet
  // (`advanceBlocked`), the primary button is greyed out so the user cannot
  // skip past the action. Steps that don't opt in are never blocked, so other
  // tours (Product Discovery, PSC) are unaffected.
  const requiresInteraction = Boolean(
    (step.data as { requiresInteraction?: boolean } | undefined)
      ?.requiresInteraction
  );
  const primaryDisabled = requiresInteraction && Boolean(advanceBlocked);

  // A step can opt out of any forward affordance entirely via
  // `data.hideForward` — the only way onward is an external action (e.g. the
  // homepage "What's new" step, where the user must close the dialog). When set
  // we render no primary button at all so there is no bypass.
  const hideForward = Boolean(
    (step.data as { hideForward?: boolean } | undefined)?.hideForward
  );

  // Symmetric to `hideForward`: a step can also opt out of the Back button via
  // `data.hideBack`. Used by the homepage "What's new" step, which must freeze
  // all tour navigation (the only way onward is closing the dialog). Default
  // off, so other tours/steps keep their Back button.
  const hideBack = Boolean(
    (step.data as { hideBack?: boolean } | undefined)?.hideBack
  );

  // When BOTH nav buttons are hidden (e.g. the What's-new step) the footer row
  // is an empty band that only adds height. Omit it entirely and drop the
  // content's bottom margin + the Paper's bottom padding so the bubble ends
  // right after the copy — clearing the slide caption beneath it. Keyed purely
  // off "both nav hidden", so any normal step (with at least one nav affordance)
  // keeps its footer and spacing unchanged.
  const omitFooter = hideForward && hideBack;

  return (
    <Paper
      ref={paperRef}
      {...tooltipProps}
      elevation={6}
      sx={{
        p: 2,
        ...(omitFooter ? { pb: 1.5 } : {}),
        maxWidth: paperMaxWidth,
        borderRadius: 2,
        position: 'relative',
      }}
    >
      <IconButton
        size="small"
        onClick={onRequestExit}
        aria-label="close"
        sx={{ position: 'absolute', top: 4, right: 4 }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      {step.title ? (
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, pr: 4 }}>
          {step.title}
        </Typography>
      ) : null}

      <Typography
        variant="body2"
        component="div"
        sx={{ mb: omitFooter ? 0 : 2 }}
      >
        {step.content as React.ReactNode}
      </Typography>

      {omitFooter ? null : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {showBack && !hideBack ? (
            <Button
              size="small"
              variant="outlined"
              onClick={backProps.onClick}
              aria-label={backProps['aria-label']}
            >
              {localeBack ?? 'Back'}
            </Button>
          ) : (
            <Box />
          )}
          <Stack direction="row" spacing={1}>
            {hideForward ? null : (
              <Button
                size="small"
                variant="contained"
                onClick={primaryProps.onClick}
                aria-label={primaryProps['aria-label']}
                disabled={primaryDisabled}
              >
                {primaryLabel}
              </Button>
            )}
          </Stack>
        </Box>
      )}
    </Paper>
  );
};

export default TourFooter;
